import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calculator } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import SEOHead from '../../components/SEOHead';
import { calculateLoanRepayment, formatCurrency } from '../../utils/financeCalc';

export default function EquipmentFinanceCalculator() {
  const [loanAmount, setLoanAmount] = useState(80000);
  const [annualRate, setAnnualRate] = useState(8.5);
  const [termYears, setTermYears] = useState(5);
  const [frequency, setFrequency] = useState('Monthly');

  const result = useMemo(
    () => calculateLoanRepayment({ loanAmount: Number(loanAmount) || 0, annualRate: Number(annualRate) || 0, termYears: Number(termYears) || 1, frequency }),
    [loanAmount, annualRate, termYears, frequency]
  );

  return (
    <>
      <SEOHead
        title="Loan Repayment Calculator | Flash Funding"
        description="Estimate loan repayments for consumer and commercial applications by amount, rate, term, and repayment frequency."
        canonical="/equipment-finance-calculator"
      />

      <section className="py-16 sm:py-20" data-testid="loan-repayment-calculator-page">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-purple-200 bg-white p-6 sm:p-8" data-testid="loan-repayment-input-card">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-purple-100 px-3 py-1 text-xs text-purple-700" data-testid="loan-repayment-icon-pill"><Calculator size={14} /> Calculator</div>
            <h1 className="mb-3 text-3xl font-heading text-[#0F0A1A] sm:text-4xl" data-testid="loan-repayment-heading">Loan Repayment Calculator</h1>
            <p className="mb-3 text-sm text-[#4A4556]" data-testid="loan-repayment-subheading">Estimate repayments, then choose the finance path that matches your needs.</p>
            <p className="mb-6 text-xs text-[#6C657E]" data-testid="loan-repayment-route-priority-text">
              Start with /car-loans, /personal-loans, /commercial-car-loans, or /business-finance. Use /apply when ready.
            </p>

            <div className="space-y-4">
              <div>
                <Label className="text-[#2E2742]">Loan Amount (AUD)</Label>
                <Input type="number" value={loanAmount} onChange={(e) => setLoanAmount(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="loan-amount-input" />
              </div>
              <div>
                <Label className="text-[#2E2742]">Interest Rate (p.a.)</Label>
                <Input type="number" step="0.01" value={annualRate} onChange={(e) => setAnnualRate(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="loan-rate-input" />
              </div>
              <div>
                <Label className="text-[#2E2742]">Term (Years)</Label>
                <Input type="number" value={termYears} onChange={(e) => setTermYears(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="loan-term-input" />
              </div>
              <div>
                <Label className="text-[#2E2742]">Repayment Frequency</Label>
                <Select value={frequency} onValueChange={setFrequency}>
                  <SelectTrigger className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="loan-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-purple-200 bg-white text-[#1B1431]">
                    {['Weekly', 'Fortnightly', 'Monthly'].map((option) => (
                      <SelectItem key={option} value={option} className="text-[#1B1431] focus:bg-purple-50" data-testid={`loan-frequency-option-${option.toLowerCase()}`}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-purple-200 bg-white p-6 sm:p-8" data-testid="loan-repayment-result-card">
            <h2 className="mb-6 text-xl font-semibold text-[#1B1431]">Estimated Repayments</h2>
            <div className="space-y-4 text-sm">
              <div className="flex justify-between border-b border-purple-100 pb-3">
                <span className="text-[#5C5671]">Repayment ({frequency})</span>
                <span className="font-mono text-purple-700" data-testid="loan-periodic-payment">{formatCurrency(result.periodicPayment)}</span>
              </div>
              <div className="flex justify-between border-b border-purple-100 pb-3">
                <span className="text-[#5C5671]">Total Repayment</span>
                <span className="font-mono text-[#1B1431]" data-testid="loan-total-repayment">{formatCurrency(result.totalRepayment)}</span>
              </div>
              <div className="flex justify-between border-b border-purple-100 pb-3">
                <span className="text-[#5C5671]">Total Interest</span>
                <span className="font-mono text-[#1B1431]" data-testid="loan-total-interest">{formatCurrency(result.totalInterest)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#5C5671]">Total Periods</span>
                <span className="font-mono text-[#1B1431]" data-testid="loan-total-periods">{result.totalPeriods}</span>
              </div>
            </div>

            <div className="mt-8 rounded-xl border border-purple-200 bg-[#f8f5fc] p-4">
              <p className="text-xs text-[#5C5671]" data-testid="loan-calc-disclaimer">Calculator outputs are estimates only and do not constitute credit approval.</p>
            </div>

            <div className="mt-8">
              <Link to="/car-loans" data-testid="loan-calculator-apply-link">
                <Button className="w-full rounded-full bg-purple-600 text-xs uppercase tracking-[0.14em] text-white hover:bg-purple-700" data-testid="loan-calculator-apply-button">
                  Check Your Options <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
